import { Layer, ObjectCategory, ZIndexes } from "@common/constants";
import { FireCellState, FireConfig } from "@common/definitions/fire";
import { mapWeather } from "@common/utils/mapWeather";
import { Vec, type Vector } from "@common/utils/vector";
import { Container } from "pixi.js";
import { Game } from "../game";
import { type FireChunk } from "../objects/fireChunk";
import { PIXI_SCALE } from "../utils/constants";
import { TrenchioSprite, toPixiCoords } from "../utils/pixi";
import { CameraManager } from "./cameraManager";
import { MapManager } from "./mapManager";
import { ParticleManager } from "./particleManager";
import { type GameSound, SoundManager } from "./soundManager";

const CHUNK_UNITS = FireConfig.cellSize * FireConfig.chunkCells;
const FLAME_FRAMES = ["fire_flame_1", "fire_flame_2", "fire_flame_3", "fire_flame_4"];
/** with more burning cells than this on screen, each cell draws one flame instead of two */
const LOW_DETAIL_CELLS = 400;
const MAX_EMBERS = 110;
const MAX_SMOKE = 70;
const MAX_CRACKLES = 3;
const CRACKLE_RANGE = 70;

interface Burning { until: number, inFire: boolean, flames: TrenchioSprite[] }

/**
 * Everything fire needs beyond drawing its cells: which cell is burning where (for the client's view of
 * who is on fire), flames on burning players, the burning warning on screen for you, crackle loops near
 * fires, ignition and sizzle sounds, and embers and smoke that drift with the map's wind.
 */
class FireEffectsClass {
    private readonly _chunks = new Map<number, FireChunk>();
    private readonly _burning = new Map<number, Burning>();
    private readonly _crackles = new Map<FireChunk, GameSound>();
    private readonly _lastIgnite = new WeakMap<FireChunk, number>();
    private _lastPour = 0;
    private _embers: number[] = [];
    private _smoke: number[] = [];
    private _overlay?: Container;
    private _vignette?: HTMLDivElement;
    private _lastCrackleCheck = 0;
    private _wind: Vector = Vec(0, 0);
    private _windSeed = -1;

    /** flames lean with the wind (radians) */
    lean = 0;
    /** too many flames on screen: draw less of each */
    lowDetail = false;

    private _key(x: number, y: number): number { return y * 4096 + x; }

    register(chunk: FireChunk): void {
        this._chunks.set(this._key(chunk.chunkX, chunk.chunkY), chunk);
    }

    unregister(chunk: FireChunk): void {
        if (this._chunks.get(this._key(chunk.chunkX, chunk.chunkY)) === chunk) this._chunks.delete(this._key(chunk.chunkX, chunk.chunkY));
        this._crackles.get(chunk)?.stop();
        this._crackles.delete(chunk);
    }

    /** the fire state at a world position, as far as this client knows */
    stateAt(position: Vector): FireCellState {
        const chunk = this._chunks.get(this._key(Math.floor(position.x / CHUNK_UNITS), Math.floor(position.y / CHUNK_UNITS)));
        if (!chunk) return FireCellState.None;
        const column = Math.floor((position.x % CHUNK_UNITS) / FireConfig.cellSize);
        const row = Math.floor((position.y % CHUNK_UNITS) / FireConfig.cellSize);
        return chunk.cellState(column, row);
    }

    private _onFire(position: Vector): boolean {
        for (const [dx, dy] of [[0, 0], [1.6, 0], [-1.6, 0], [0, 1.6], [0, -1.6]]) {
            if (this.stateAt(Vec(position.x + dx, position.y + dy)) === FireCellState.Burning) return true;
        }
        return false;
    }

    /** a chunk's cells changed: sound the ignition or the sizzle when enough caught or went out at once */
    chunkChanged(chunk: FireChunk, lit: number, putOut: number): void {
        const now = Date.now();
        const center = chunk.worldCenter;
        if (lit >= 4 && now - (this._lastIgnite.get(chunk) ?? 0) > 1200) {
            this._lastIgnite.set(chunk, now);
            SoundManager.play("fire_ignite", { position: center, falloff: 0.5, maxRange: 96, layer: Layer.Ground });
        }
        if (putOut >= 4) SoundManager.play("fire_extinguish", { position: center, falloff: 0.5, maxRange: 80, layer: Layer.Ground });
    }

    /** fuel landed on the ground: a pouring gurgle there (at most every 0.9 s, near you) */
    fuelPoured(chunk: FireChunk, cells: number): void {
        const now = Date.now();
        if (cells <= 0 || now - this._lastPour < 900) return;
        this._lastPour = now;
        const size = FireConfig.cellSize * FireConfig.chunkCells;
        SoundManager.play("fuel_pour", { position: Vec(chunk.chunkX * size + size / 2, chunk.chunkY * size + size / 2), falloff: 0.6, maxRange: 48, layer: Layer.Ground });
    }

    /** embers and smoke rising from a burning chunk (called by the chunk every frame) */
    emit(chunk: FireChunk, now: number): void {
        const cells = chunk.burningCells;
        if (cells === 0) return;
        const frame = Math.min(0.05, Game.serverDt ? 1 / 60 : 1 / 60);
        this._embers = this._embers.filter(end => end > now);
        this._smoke = this._smoke.filter(end => end > now);
        const origin = chunk.worldCenter;
        const spread = Math.min(CHUNK_UNITS / 2, 2 + Math.sqrt(cells) * 1.6);
        if (this._embers.length < MAX_EMBERS && Math.random() < cells * 0.9 * frame) {
            const lifetime = 700 + Math.random() * 700;
            this._embers.push(now + lifetime);
            ParticleManager.spawnParticle({
                frames: "fire_ember",
                position: Vec(origin.x + (Math.random() - 0.5) * spread * 2, origin.y + (Math.random() - 0.5) * spread * 2),
                lifetime,
                zIndex: ZIndexes.ObstaclesLayer3 + 0.6,
                layer: Layer.Ground,
                scale: { start: 0.6 + Math.random() * 0.5, end: 0.1 },
                alpha: { start: 1, end: 0 },
                speed: Vec(this._wind.x * 1.8 + (Math.random() - 0.5) * 3, this._wind.y * 1.8 - 2.5 - Math.random() * 3)
            });
        }
        if (this._smoke.length < MAX_SMOKE && Math.random() < cells * 0.28 * frame) {
            const lifetime = 2200 + Math.random() * 1400;
            this._smoke.push(now + lifetime);
            ParticleManager.spawnParticle({
                frames: "smoke_particle",
                position: Vec(origin.x + (Math.random() - 0.5) * spread * 1.6, origin.y + (Math.random() - 0.5) * spread * 1.6),
                lifetime,
                zIndex: ZIndexes.ObstaclesLayer3 + 0.7,
                layer: Layer.Ground,
                scale: { start: 1.2 + Math.random() * 0.6, end: 3.6 + Math.random() * 1.4 },
                alpha: { start: 0.36, end: 0 },
                rotation: { start: Math.random() * 6.28, end: Math.random() * 6.28 },
                tint: 0x38332c,
                speed: Vec(this._wind.x * 2.4 + (Math.random() - 0.5), this._wind.y * 2.4 - 1.2)
            });
        }
    }

    update(): void {
        if (!Game.gameStarted) return;
        const now = Date.now();

        // the map's wind, the same one the rain and fog use
        if (MapManager.seed !== this._windSeed) {
            this._windSeed = MapManager.seed;
            const weather = mapWeather(MapManager.seed);
            this._wind = Vec(Math.cos(weather.windAngle) * weather.windSpeed, Math.sin(weather.windAngle) * weather.windSpeed);
            this.lean = Math.max(-0.22, Math.min(0.22, this._wind.x * 0.14));
        }

        let burningCells = 0;
        for (const chunk of this._chunks.values()) burningCells += chunk.burningCells;
        this.lowDetail = burningCells > LOW_DETAIL_CELLS;

        this._updatePlayers(now);
        if (now - this._lastCrackleCheck > 300) {
            this._lastCrackleCheck = now;
            this._updateCrackles();
        }
    }

    /** who is on fire, as this client sees it: standing in flames, and for 3 seconds after */
    private _updatePlayers(now: number): void {
        const seen = new Set<number>();
        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            if (player.dead || player.layer !== Layer.Ground) continue;
            const inFire = this._onFire(player.position);
            let burning = this._burning.get(player.id);
            if (inFire) {
                burning ??= { until: 0, inFire: true, flames: [] };
                burning.until = now + FireConfig.damage.burnMs;
                this._burning.set(player.id, burning);
            }
            if (!burning) continue;
            burning.inFire = inFire;
            if (now >= burning.until) {
                for (const flame of burning.flames) flame.destroy();
                this._burning.delete(player.id);
                continue;
            }
            seen.add(player.id);
            this._drawPlayerFlames(burning, player.position, now);
        }
        for (const [id, burning] of this._burning) {
            if (seen.has(id)) continue;
            for (const flame of burning.flames) flame.destroy();
            this._burning.delete(id);
        }

        // you: the screen edges glow with fire, and the health strap turns orange
        const me = Game.activePlayer ? this._burning.get(Game.activePlayer.id) : undefined;
        const level = !me ? 0 : me.inFire ? 1 : 0.55 * Math.min(1, (me.until - now) / 1500 + 0.3);
        this._showWarning(level);
    }

    private _drawPlayerFlames(burning: Burning, position: Vector, now: number): void {
        if (!this._overlay) {
            this._overlay = new Container();
            this._overlay.zIndex = ZIndexes.Players + 0.4;
            CameraManager.getContainer(Layer.Ground).addChild(this._overlay);
        }
        while (burning.flames.length < 3) {
            const flame = new TrenchioSprite(FLAME_FRAMES[burning.flames.length]);
            flame.anchor.set(0.5, 0.9);
            flame.blendMode = "add";
            this._overlay.addChild(flame);
            burning.flames.push(flame);
        }
        const fade = Math.min(1, (burning.until - now) / 600);
        const center = toPixiCoords(position);
        burning.flames.forEach((flame, k) => {
            const angle = k * 2.1 + now * 0.0015;
            flame.position.set(center.x + Math.cos(angle) * 1.1 * PIXI_SCALE, center.y + Math.sin(angle) * 0.9 * PIXI_SCALE + 0.9 * PIXI_SCALE);
            flame.setFrame(FLAME_FRAMES[Math.floor(now / 85 + k * 3) % FLAME_FRAMES.length]);
            const size = (burning.inFire ? 0.95 : 0.62) * (0.9 + 0.15 * Math.sin(now * 0.02 + k * 2));
            flame.scale.set(size * 0.8, size);
            flame.alpha = 0.85 * fade;
            flame.rotation = this.lean;
        });
    }

    /** the last warning level shown (the page is only touched when it changes: this runs every frame) */
    private _warningShown = "";

    private _showWarning(level: number): void {
        const shown = level.toFixed(2);
        if (shown === this._warningShown) return;
        this._warningShown = shown;
        if (!this._vignette) {
            this._vignette = document.createElement("div");
            this._vignette.id = "fire-vignette";
            document.body.appendChild(this._vignette);
        }
        this._vignette.style.opacity = shown;
        this._vignette.classList.toggle("on", level > 0);
        document.getElementById("health-bar-container")?.classList.toggle("th-burning", level > 0);
    }

    /** looping crackles at the nearest few fires */
    private _updateCrackles(): void {
        const listener = Game.activePlayer?.position;
        const wanted = new Set<FireChunk>();
        if (listener) {
            [...this._chunks.values()]
                .filter(chunk => chunk.burningCells > 0)
                .map(chunk => ({ chunk, distance: Vec.len(Vec.sub(chunk.worldCenter, listener)) }))
                .filter(({ distance }) => distance < CRACKLE_RANGE)
                .sort((a, b) => a.distance - b.distance)
                .slice(0, MAX_CRACKLES)
                .forEach(({ chunk }) => wanted.add(chunk));
        }
        for (const [chunk, sound] of this._crackles) {
            if (wanted.has(chunk)) continue;
            sound.stop();
            this._crackles.delete(chunk);
        }
        for (const chunk of wanted) {
            if (this._crackles.has(chunk)) continue;
            // dynamic: it grows louder as you come closer and fades as you walk away (its volume was the one it started at)
            this._crackles.set(chunk, SoundManager.play("fire_crackle", { position: chunk.worldCenter, falloff: 0.6, maxRange: CRACKLE_RANGE, loop: true, dynamic: true, layer: Layer.Ground }));
        }
    }

    /** a match ended: forget everything */
    reset(): void {
        for (const sound of this._crackles.values()) sound.stop();
        this._crackles.clear();
        for (const burning of this._burning.values()) for (const flame of burning.flames) flame.destroy();
        this._burning.clear();
        this._chunks.clear();
        this._overlay?.destroy({ children: true });
        this._overlay = undefined;
        this._showWarning(0);
    }
}

export const FireEffects = new FireEffectsClass();
